import React, { createContext, useContext, useState, useEffect } from 'react';
import { preferencesAPI } from '../utils/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Load from localStorage first for instant load
    return localStorage.getItem('theme') || 'dark';
  });
  const [primaryColor, setPrimaryColor] = useState(() => {
    return localStorage.getItem('primaryColor') || '#22c55e';
  });

  // Apply theme immediately on mount and changes
  useEffect(() => {
    applyTheme(theme, primaryColor);
  }, [theme, primaryColor]);

  // Load preferences from API after mount
  useEffect(() => {
    loadPreferencesFromAPI();
  }, []);

  const loadPreferencesFromAPI = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        const response = await preferencesAPI.get();
        const serverTheme = response.data.theme || 'dark';
        const serverColor = response.data.primary_color || '#22c55e';

        // Only update if different from current
        if (serverTheme !== theme) {
          setTheme(serverTheme);
          localStorage.setItem('theme', serverTheme);
        }
        if (serverColor !== primaryColor) {
          setPrimaryColor(serverColor);
          localStorage.setItem('primaryColor', serverColor);
        }
      }
    } catch (error) {
      console.log('Using default theme');
    }
  };

  const applyTheme = (newTheme, color) => {
    // Apply to root HTML element
    document.documentElement.setAttribute('data-theme', newTheme);
    document.body.setAttribute('data-theme', newTheme);

    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }

    // Update CSS variables for primary color
    const root = document.documentElement;
    const hsl = hexToHSL(color);
    root.style.setProperty('--primary', hsl);
    root.style.setProperty('--ring', hsl);
    root.style.setProperty('--primary-solid', color); // Solid color for backgrounds
  };

  const hexToHSL = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '142 76% 36%';

    let r = parseInt(result[1], 16) / 255;
    let g = parseInt(result[2], 16) / 255;
    let b = parseInt(result[3], 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    h = Math.round(h * 360);
    s = Math.round(s * 100);
    l = Math.round(l * 100);

    return `${h} ${s}% ${l}%`;
  };

  const updateTheme = async (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme, primaryColor);

    try {
      await preferencesAPI.update({ theme: newTheme });
    } catch (error) {
      console.error('Failed to save theme preference');
    }
  };

  const updatePrimaryColor = async (color) => {
    setPrimaryColor(color);
    localStorage.setItem('primaryColor', color);
    applyTheme(theme, color);

    try {
      await preferencesAPI.update({ primary_color: color });
    } catch (error) {
      console.error('Failed to save color preference');
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        primaryColor,
        updateTheme,
        updatePrimaryColor,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
